import AppActivityIndicator from "@toothfairy/shared-ui/AppActivityIndicator";
import AppForms from "@toothfairy/shared-ui/AppForms";
import { AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, TextInput } from "react-native";

import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyJsonViewer from "../../../../Components/ToothFairyComponents/ToothFairyJsonViewer";
import ToolTesting from "../../../../API/ToolTesting";

const _typedDefault = (propSchema) => {
  if (!propSchema || typeof propSchema !== "object") return "";
  const t = propSchema.type;
  if (t === "array") return [];
  if (t === "object") return {};
  if (t === "boolean") return false;
  if (t === "number" || t === "integer") return 0;
  return "";
};

const _coerceObject = (val) => {
  if (!val) return {};
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }
  if (typeof val === "object" && !Array.isArray(val)) return val;
  return {};
};

const buildPrefilledArgs = (parameters, staticArgs) => {
  const paramSchema = _coerceObject(parameters);
  const properties = paramSchema.properties;
  const paramDefaults = {};
  if (properties && typeof properties === "object") {
    for (const [key, propDef] of Object.entries(properties)) {
      paramDefaults[key] = _typedDefault(propDef);
    }
  }
  const statics = _coerceObject(staticArgs);
  return { ...paramDefaults, ...statics };
};

/**
 * ToolTestPanel — inline isolated tool / hook test surface.
 *
 * The same testing UI that used to live inside ToolTestModal, rendered as a
 * plain panel so it can be embedded both in the modal (settings quick-test)
 * and as the dedicated Test tab of the Tool Builder.
 *
 * Wraps the ChatECS `POST /test/tool` and `POST /test/hook` endpoints exposed
 * by `API/ToolTesting`. Executes a SINGLE configured AgentFunction
 * (mode="tool") or a SINGLE Custom Execution Environment hook (mode="hook")
 * with explicit JSON arguments — no LLM tool-selection loop — and inspects the
 * raw result.
 *
 * Remount the panel (change React `key`) to reset the arguments editor and the
 * last result — the modal wrapper does this on every open.
 *
 * Props:
 *   mode                         — "tool" | "hook"
 *   id                           — functionId (tool) or environmentId (hook)
 *   token                        — workspace API key (x-api-key)
 *   parameters                   — function's `parameters` JSON schema (tool mode);
 *                                  used to auto-populate the editor scaffold
 *   staticArgs                   — function's `staticArgs` (tool mode); merged into
 *                                  the pre-filled editor so configured values show
 */
const ToolTestPanel = ({
  mode = "tool",
  id,
  token,
  parameters,
  staticArgs,
}) => {
  const { t } = useTranslation();
  const { currentTheme, mode: themeMode } = AppTheme.useTheme();
  const AppToasty = AppToast.useToast();

  const prefilledArgs = useMemo(
    () => buildPrefilledArgs(parameters, staticArgs),
    [parameters, staticArgs]
  );

  const argsRef = useRef(prefilledArgs);
  const [customerId, setCustomerId] = useState("");
  const [caseId, setCaseId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isTool = mode === "tool";

  const runTest = async () => {
    if (!id) {
      AppToasty.show(
        isTool
          ? t("tools.testSaveFirst", {
              defaultValue: "Save the tool before testing.",
            })
          : t("tools.testSaveEnvFirst", {
              defaultValue: "Save the environment before testing.",
            }),
        { placement: "bottom", type: "danger" }
      );
      return;
    }
    const args = argsRef.current;
    if (!args || typeof args !== "object" || Array.isArray(args)) {
      AppToasty.show(
        t("tools.testInvalidJson", {
          defaultValue: "Arguments must be a valid JSON object",
        }),
        { placement: "bottom", type: "danger" }
      );
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const chatData =
        isTool && (customerId || caseId)
          ? {
              ...(customerId ? { customerId } : {}),
              ...(caseId ? { caseId } : {}),
            }
          : undefined;

      const response = isTool
        ? await ToolTesting.testTool({
            token,
            functionId: id,
            arguments: args,
            chatData,
          })
        : await ToolTesting.testHook({
            token,
            environmentId: id,
            arguments: args,
          });

      setResult(response);
      const ok = response?.success === true;
      AppToasty.show(
        ok
          ? t("tools.testSucceeded", { defaultValue: "Test succeeded" })
          : response?.error ||
              t("tools.testFailed", { defaultValue: "Test failed" }),
        { placement: "bottom", type: ok ? "success" : "danger" }
      );
    } catch (e) {
      setResult({
        success: false,
        transport: false,
        httpStatus: null,
        problem: "CLIENT_EXCEPTION",
        type: null,
        toolSuccess: null,
        result: null,
        status_code: null,
        error: e?.message || String(e),
      });
      AppToasty.show(e?.message || String(e), {
        placement: "bottom",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    borderWidth: 1,
    borderColor: currentTheme?.theme?.lightGrey || "#ccc",
    borderRadius: 8,
    padding: 10,
    minHeight: 40,
    marginVertical: 6,
    fontFamily: "monospace",
    color: currentTheme?.theme?.black || "#000",
    backgroundColor: currentTheme?.theme?.white || "#fff",
  };

  return (
    <View style={{ padding: 16, gap: 12 }}>
      <View>
        <AppForms.AppForm
          initialValues={{ arguments: prefilledArgs }}
          onValuesChange={(values) => {
            argsRef.current = values?.arguments ?? {};
          }}
        >
          <AppForms.AppFormFieldJSONV2
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            textInputLabelStyle={{
              marginLeft: 10,
            }}
            wrapperContainer={{
              height: 320,
              paddingTop: 10,
            }}
            mode="code"
            darkMode={themeMode === "dark"}
            label={t("tools.testArguments", {
              defaultValue: "Arguments (JSON)",
            })}
            placeholder={t("tools.testArgumentsPlaceholder", {
              defaultValue: '{ "key": "value" }',
            })}
            name="arguments"
            onValidate={(json) => {
              const errors = [];
              if (json === null || json === undefined) {
                return errors;
              }
              if (typeof json !== "object" || Array.isArray(json)) {
                errors.push({
                  message: t("tools.testArgumentsMustBeObject", {
                    defaultValue: "Arguments must be a JSON object",
                  }),
                });
              }
              return errors;
            }}
          />
        </AppForms.AppForm>
      </View>

      {isTool && (
        <View
          style={{
            flexDirection: "column",
            gap: 10,
            marginLeft: 10,
            marginTop: -10,
          }}
        >
          <View style={{ flex: 1 }}>
            <AppText style={{ fontWeight: "bold", marginBottom: 4 }}>
              {t("tools.testCustomerId", {
                defaultValue: "Customer ID (optional)",
              })}
            </AppText>
            <TextInput
              value={customerId}
              onChangeText={setCustomerId}
              style={inputStyle}
              placeholder="tfCustomerId"
              placeholderTextColor={currentTheme?.theme?.grey || "#999"}
            />
          </View>
          <View style={{ flex: 1 }}>
            <AppText style={{ fontWeight: "bold", marginBottom: 4 }}>
              {t("tools.testCaseId", { defaultValue: "Case ID (optional)" })}
            </AppText>
            <TextInput
              value={caseId}
              onChangeText={setCaseId}
              style={inputStyle}
              placeholder="tfCaseId"
              placeholderTextColor={currentTheme?.theme?.grey || "#999"}
            />
          </View>
        </View>
      )}

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          marginLeft: 0,
        }}
      >
        <ToothFairyButton
          title={t("tools.runTest", { defaultValue: "Run Test" })}
          fontColor={(loading || !id) && currentTheme?.theme?.white}
          onPress={runTest}
          isDisabled={loading || !id}
        />
        {loading && <AppActivityIndicator />}
      </View>

      {result && (
        <View style={{ marginTop: 8 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              marginBottom: 8,
              padding: 8,
              borderRadius: 6,
              backgroundColor: result.success
                ? (currentTheme?.theme?.green || "#070") + "15"
                : (currentTheme?.theme?.red || "#c00") + "15",
            }}
          >
            <AppText
              style={{
                fontWeight: "bold",
              }}
            >
              {result.success
                ? t("tools.testStatusSuccess", { defaultValue: "SUCCESS" })
                : t("tools.testStatusFailed", { defaultValue: "FAILED" })}
            </AppText>
            {result.type && (
              <AppText style={{ opacity: 0.7 }}>type: {result.type}</AppText>
            )}
            {result.httpStatus != null && (
              <AppText style={{ opacity: 0.7 }}>
                HTTP {result.httpStatus}
              </AppText>
            )}
            {result.status_code != null &&
              String(result.status_code) !== String(result.httpStatus) && (
                <AppText style={{ opacity: 0.7 }}>
                  tool status: {result.status_code}
                </AppText>
              )}
            {result.problem && (
              <AppText
                style={{
                  opacity: 0.8,
                  fontStyle: "italic",
                  color: currentTheme?.theme?.white,
                }}
              >
                {result.problem}
              </AppText>
            )}
          </View>

          <View style={{ marginBottom: 8, gap: 2 }}>
            <View style={{ flexDirection: "row", gap: 6 }}>
              <AppText style={{ fontWeight: "bold" }}>
                {t("tools.testLayerTransport", {
                  defaultValue: "Transport:",
                })}
              </AppText>
              <AppText
                style={{
                  color: currentTheme?.theme?.white,
                }}
              >
                {result.transport
                  ? t("tools.testTransportOk", {
                      defaultValue: "reached server",
                    })
                  : t("tools.testTransportFailed", {
                      defaultValue: "request did not reach server",
                    })}
              </AppText>
            </View>
            <View style={{ flexDirection: "row", gap: 6 }}>
              <AppText style={{ fontWeight: "bold" }}>
                {t("tools.testLayerTool", { defaultValue: "Tool exec:" })}
              </AppText>
              <AppText
                style={{
                  color:
                    result.toolSuccess === false
                      ? currentTheme?.theme?.red || "#c00"
                      : result.toolSuccess === true
                      ? currentTheme?.theme?.green || "#070"
                      : currentTheme?.theme?.grey || "#999",
                }}
              >
                {result.toolSuccess === false
                  ? t("tools.testToolFailed", {
                      defaultValue: "tool reported failure",
                    })
                  : result.toolSuccess === true
                  ? t("tools.testToolOk", {
                      defaultValue: "tool reported success",
                    })
                  : t("tools.testToolUnknown", { defaultValue: "n/a" })}
              </AppText>
            </View>
          </View>

          {result.error && (
            <View
              style={{
                marginBottom: 8,
                padding: 8,
                borderRadius: 6,
                backgroundColor: (currentTheme?.theme?.red || "#c00") + "10",
              }}
            >
              <AppText
                style={{
                  color: currentTheme?.theme?.white,
                  fontWeight: "bold",
                  marginBottom: 2,
                }}
              >
                {t("tools.testErrorLabel", { defaultValue: "Error" })}
              </AppText>
              <AppText
                style={{
                  color: currentTheme?.theme?.white,
                  fontFamily: "monospace",
                }}
              >
                {String(result.error)}
              </AppText>
            </View>
          )}

          <AppText
            style={{
              fontWeight: "bold",
              marginBottom: 4,
              opacity: 0.8,
            }}
          >
            {t("tools.testResultPayload", {
              defaultValue: "Result payload",
            })}
          </AppText>
          <ToothFairyJsonViewer
            isEmpty={!result.result}
            data={result.result}
            mainCaption={t("tools.testNoResult", {
              defaultValue: "No result data",
            })}
          />
        </View>
      )}
    </View>
  );
};

export default ToolTestPanel;
